import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import PageHero from "./PageHero";
import { waLink } from "../../brand";

/**
 * Reusable inner page builder for advertising platform pages
 * (Google Ads, Meta Ads, TikTok Ads). Keeps exact visual identity:
 * grad-border, glass, gradient text, card-lift, animations.
 */
export default function AdsPlatformPage({
  meta,
  hero,
  intro,
  services,    // array of { icon, title, desc }
  approach,    // array of strings (bullets)
  whyItems,    // array of { title, desc }
  faqs,        // array of { q, a }
  related,     // array of { to, label }
  resultStat,  // { value, label, footnote }
}) {
  return (
    <main>
      <PageHero
        eyebrow={meta.eyebrow}
        title={hero.title}
        highlight={hero.highlight}
        titleSuffix={hero.suffix || "."}
        description={hero.description}
        primaryCta={{ to: "/contacto", label: "Solicitar diagnóstico" }}
        secondaryCta={{ href: waLink(), label: "Hablar por WhatsApp" }}
      />

      {/* Intro band */}
      <section className="relative pb-12">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end reveal">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-4">{intro.eyebrow}</div>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                {intro.title}
              </h2>
            </div>
            <p className="lg:col-span-5 text-zinc-400 text-lg leading-relaxed">{intro.body}</p>
          </div>
        </div>
      </section>

      {/* Services / what I do */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Qué implemento</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              No vendo gestión de campañas. <span className="text-gradient">Implemento sistemas de adquisición.</span>
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                className="reveal grad-border p-7 card-lift"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-brand grid place-items-center text-black/90 mb-5">
                  <Icon className="w-5 h-5" strokeWidth={2.4} />
                </div>
                <h3 className="font-display font-semibold text-white text-lg leading-snug">{title}</h3>
                <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 reveal">
              <div className="eyebrow mb-5">Mi enfoque</div>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                Performance real, no impresiones <span className="text-gradient">vanidosas</span>.
              </h2>
              <p className="mt-5 text-zinc-400 max-w-md">
                Cada euro que entra a una plataforma debe poder atribuirse a un lead, una conversación y una venta. Si no, no es marketing — es entretenimiento.
              </p>
              {resultStat && (
                <div className="mt-8 grad-border p-6 inline-flex flex-col">
                  <div className="font-display font-extrabold text-5xl text-gradient text-glow tracking-tighter">
                    {resultStat.value}
                  </div>
                  <div className="text-zinc-300 text-sm mt-1 max-w-[22ch]">{resultStat.label}</div>
                  {resultStat.footnote && (
                    <div className="text-zinc-500 text-xs mt-2">{resultStat.footnote}</div>
                  )}
                </div>
              )}
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
              {approach.map((b, i) => (
                <div key={b} className="reveal grad-border p-5" style={{ transitionDelay: `${i * 40}ms` }}>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#ff8a00] mt-0.5 shrink-0" />
                    <p className="text-zinc-200 text-[15px] leading-relaxed">{b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why work with me on this platform */}
      {whyItems && whyItems.length > 0 && (
        <section className="relative py-20">
          <div className="container-x">
            <div className="reveal max-w-2xl">
              <div className="eyebrow mb-5">Por qué Daos Ads</div>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                Operador real, no <span className="text-gradient">gestor de cuentas</span>.
              </h2>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {whyItems.map(({ title, desc }, i) => (
                <div key={title} className="reveal grad-border p-6" style={{ transitionDelay: `${i * 50}ms` }}>
                  <div className="text-white font-display font-semibold text-base">{title}</div>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="relative py-20">
          <div className="container-x">
            <div className="reveal max-w-2xl">
              <div className="eyebrow mb-5">Preguntas frecuentes</div>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                Lo que probablemente <span className="text-gradient">te estás preguntando</span>.
              </h2>
            </div>
            <div className="mt-10 grid md:grid-cols-2 gap-4">
              {faqs.map(({ q, a }, i) => (
                <div key={q} className="reveal grad-border p-6" style={{ transitionDelay: `${i * 50}ms` }}>
                  <div className="text-white font-display font-semibold">{q}</div>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related pages / interlinking */}
      {related && related.length > 0 && (
        <section className="relative py-20">
          <div className="container-x">
            <div className="reveal max-w-2xl">
              <div className="eyebrow mb-5">Continúa explorando</div>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                Servicios <span className="text-gradient">conectados</span>.
              </h2>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="reveal grad-border p-6 card-lift block"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-white font-display font-semibold">{r.label}</div>
                    <ArrowUpRight className="w-5 h-5 text-zinc-400" />
                  </div>
                  {r.desc && <p className="mt-2 text-zinc-400 text-sm">{r.desc}</p>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA tail */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal grad-border p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#ff3b1f]/25 via-[#ff8a00]/20 to-transparent rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-5xl tracking-tighter leading-[1.05]">
                ¿Listo para convertir tu inversión en <span className="text-gradient">ventas reales</span>?
              </h2>
              <p className="mt-5 text-zinc-400 max-w-2xl mx-auto">
                Cuéntame qué plataformas usas hoy, tu inversión actual y tu objetivo. En menos de 24 h te respondo personalmente.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/contacto" className="btn-primary">
                  Agenda una consultoría <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={waLink()} target="_blank" rel="noreferrer" className="btn-secondary">
                  <span className="w-2 h-2 rounded-full bg-[#25D366] pulse-dot" />
                  Hablar por WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
